/**
 * avatarResolver.ts
 *
 * Pure module that maps NFT token IDs to the existing game skin catalog.
 * Token ID N maps to skin_NNN (the same characters in the store).
 * No side effects — all functions are deterministic given their inputs.
 */

import { getCharacterById, type CharacterDef } from "../economy/characters";

const DEFAULT_CHARACTER_STORAGE_BASE = "https://svzlccqnnyhrhozrfnxj.supabase.co/storage/v1/object/public/characters";
const CHARACTER_STORAGE_BASE = import.meta.env.VITE_CHARACTER_STORAGE_BASE ?? DEFAULT_CHARACTER_STORAGE_BASE;

export interface AvatarDisplay {
  tokenId: number;
  avatarId: string;     // skin ID e.g. "skin_042"
  displayName: string;  // e.g. "BitBeast #42"
  imagePath: string;    // Supabase Storage URL for NFT image
  color: string;        // deterministic BG color from catalog
  rarity: string;       // COMMON / RARE / EPIC
  isEquipped: boolean;
  characterDef: CharacterDef | null;
  source: "direct" | "delegated"; // ownership source (Phase 33 delegate.xyz)
  vaultAddress?: string;           // cold vault address when source is "delegated"
}

/**
 * Returns the skin ID for a given token ID.
 * Token ID 1 -> "skin_001", token ID 42 -> "skin_042", etc.
 * Maps directly into the existing CHARACTER_CATALOG.
 */
export function tokenIdToAvatarId(tokenId: number): string {
  return `skin_${String(tokenId).padStart(3, "0")}`;
}

/**
 * Maps an array of token IDs to AvatarDisplay objects.
 * Sorted ascending by tokenId.
 * Sets isEquipped=true for the avatar matching equippedCharacter.
 * Uses the existing character catalog for images and metadata.
 *
 * @param delegatedTokenIds - Optional map of vault address -> token IDs from that vault.
 *   When provided, tokens present in this map are marked source="delegated" with vaultAddress set.
 *   Tokens not in the map are marked source="direct". When omitted, all tokens are "direct".
 */
export function resolveAvatarList(
  tokenIds: number[],
  equippedCharacter: string | null,
  delegatedTokenIds?: Map<string, number[]> | null,
): AvatarDisplay[] {
  // Build a lookup: token_id -> vault address for delegated tokens
  const delegatedLookup = new Map<number, string>();
  if (delegatedTokenIds != null) {
    for (const [vault, ids] of delegatedTokenIds) {
      for (const id of ids) {
        delegatedLookup.set(id, vault);
      }
    }
  }

  const sorted = [...tokenIds].sort((a, b) => a - b);
  return sorted.map((tokenId) => {
    const avatarId = tokenIdToAvatarId(tokenId);
    const charDef = getCharacterById(avatarId) ?? null;
    const vaultAddress = delegatedLookup.get(tokenId);
    const source: "direct" | "delegated" = vaultAddress != null ? "delegated" : "direct";
    return {
      tokenId,
      avatarId,
      displayName: `BitBeast #${tokenId}`,
      imagePath: `${CHARACTER_STORAGE_BASE}/${tokenId}.png`,
      color: charDef?.color ?? "#5F27CD",
      rarity: charDef?.rarity ?? "COMMON",
      isEquipped: avatarId === equippedCharacter,
      characterDef: charDef,
      source,
      ...(vaultAddress != null ? { vaultAddress } : {}),
    };
  });
}

/**
 * Returns true if the cache is stale (older than ttlHours or never synced).
 * Stale means: lastSyncAt is null OR (now - lastSyncAt) >= ttlHours.
 */
export function isCacheStale(lastSyncAt: string | null, ttlHours: number = 6): boolean {
  if (lastSyncAt == null) return true;
  try {
    const diffMs = Date.now() - new Date(lastSyncAt).getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours >= ttlHours;
  } catch {
    return true;
  }
}

/**
 * Returns a human-readable string for how long ago a sync occurred.
 * Examples: "Never", "Just now", "5 min ago", "2 hours ago", "1 day ago"
 */
export function formatSyncAge(lastSyncAt: string | null): string {
  if (lastSyncAt == null) return "Never";
  try {
    const diffMs = Date.now() - new Date(lastSyncAt).getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return "Just now";
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return diffHr === 1 ? "1 hour ago" : `${diffHr} hours ago`;
    const diffDay = Math.floor(diffHr / 24);
    return diffDay === 1 ? "1 day ago" : `${diffDay} days ago`;
  } catch {
    return "Never";
  }
}
